import { GlobalEvents } from '@highlight/ipc'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import {
  fetchOrganizationSkills,
  setOrganizationSkillSubscription,
  type OrganizationSkill,
  type OrganizationSkillSubscriptionInput,
} from './organization-skills-api'
import { SKILLS_CHANGED_EVENT } from './useSkills'

export const ORGANIZATION_SKILLS_QUERY_KEY = 'organization-skills'

export type { OrganizationSkill, OrganizationSkillSubscriptionInput } from './organization-skills-api'

export const useOrganizationSkillDirectories = (organizationIds: Array<string>) => {
  const { getAuthToken } = useAuth()

  return useQueries({
    queries: organizationIds.map((organizationId) => ({
      queryKey: [ORGANIZATION_SKILLS_QUERY_KEY, organizationId],
      queryFn: async (): Promise<Array<OrganizationSkill>> => {
        const token = await getAuthToken()
        return fetchOrganizationSkills(organizationId, token)
      },
      staleTime: 1000 * 60 * 10,
    })),
  })
}

export const useSetOrganizationSkillSubscription = () => {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['set-organization-skill-subscription'],
    mutationFn: async (input: OrganizationSkillSubscriptionInput): Promise<void> => {
      const token = await getAuthToken()
      await setOrganizationSkillSubscription(input, token)
    },
    onSuccess: async (_, input) => {
      await queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_SKILLS_QUERY_KEY, input.organizationId],
      })
      GlobalEvents.emit(SKILLS_CHANGED_EVENT)
    },
  })
}
